// global imports
import { Dialog, DialogContent, DialogTitle, makeStyles, Theme, Typography } from '@material-ui/core';
import DialogActions from '@material-ui/core/DialogActions';
import { StyleRules } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import { withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';

// project imports
import Button from '../../components/CustomButtons/Button.jsx';
import { config } from '../../config';
import { logoutRequest, putUserInfoRequest } from '../../store/actions/auth_actions';
import Checkbox from '../Checkbox/Checkbox';
import LanguageToggle from '../LanguageToggle';

// project types imports
import { IState } from '../../store/reducers';
import { IStyleProps, PropsClasses } from '../../types/StyleProps';

// local imports
import baseStyle from './styles';
import { DispatchProps, OwnProps, Props, StateProps } from './types';

const useStyles = makeStyles<Theme, IStyleProps>(() => baseStyle as StyleRules);

const TermsGuard: React.FC<Props> = props => {
  const classes: PropsClasses = useStyles({} as IStyleProps);

  // tslint:disable-next-line:no-shadowed-variable
  const { translate, putUserInfoRequest } = props;

  const [termsAccepted, setTermsAccepted] = useState(true);
  const [accepted, setAccepted] = useState(false);

  // test connection status
  useEffect(() => {
    if (props.userInfo && props.userInfo.termsAndConditions) {
      if (!props.userInfo.termsAndConditions.find((terms: any) => terms.version === config.termsVersion)) {
        putUserInfoRequest({
          termsAndConditions: [
            ...props.userInfo.termsAndConditions,
            {
              confirmed: null,
              version: config.termsVersion,
            },
          ],
        });
      }
      if (props.userInfo.termsAndConditions.find((terms: any) => !terms.confirmed)) {
        setTermsAccepted(false);
      }
    }
  }, [props.userInfo]);

  const handleClose = () => {
    setTermsAccepted(true);
  };

  const handleAccept = () => {
    putUserInfoRequest({
      termsAndConditions: props.userInfo.termsAndConditions.map((terms: any) =>
        terms.confirmed
          ? terms
          : {
              confirmed: Date.now(),
              version: terms.version,
            },
      ),
    });
    setTermsAccepted(true);
  };

  const handleCancel = () => {
    props.logoutRequest({});
    setTermsAccepted(true);
  };

  return props.userInfo && props.userInfo.termsAndConditions && props.userInfo.termsAndConditions.length ? (
    <Dialog
      disableBackdropClick={true}
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={!termsAccepted}
    >
      <DialogTitle disableTypography={true} className={classes.root}>
        <Typography variant="h6">{translate('terms_and_conditions.terms_and_conditions')}</Typography>
      </DialogTitle>
      <DialogContent dividers={true} style={{ textAlign: 'center' }}>
        <Typography gutterBottom={true}>
          {translate('terms_and_conditions.terms_and_conditions_details', undefined, { renderInnerHtml: true })}
        </Typography>
        <Typography gutterBottom={true}>
          <Checkbox
            name="acceptTermsAndConditions"
            onChange={() => setAccepted(!accepted)}
            checked={accepted}
            label={translate('terms_and_conditions.terms_and_conditions_accept') as string}
            classes={{
              label: classes.blackColor,
            }}
          />
        </Typography>
      </DialogContent>
      <DialogActions>
        <LanguageToggle big={true} />
        <div style={{ flex: '1 0 0' }} />
        <Button autoFocus={true} onClick={handleCancel} color="primary">
          {translate('terms_and_conditions.dismiss')}
        </Button>
        <Button onClick={handleAccept} disabled={!accepted} color="primary">
          {translate('terms_and_conditions.next')}
        </Button>
      </DialogActions>
    </Dialog>
  ) : (
    <></>
  );
};

const mapStateToProps = (state: IState): StateProps => ({
  userInfo: state.auth.userInfo,
});

const mapDispatchToProps = {
  logoutRequest,
  putUserInfoRequest,
};

export default connect<StateProps, DispatchProps, OwnProps, any>(
  mapStateToProps,
  mapDispatchToProps,
)(withLocalize(TermsGuard));
